import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, Divider, Tabs, Tab } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import LocationsTab from './components/LocationsTab';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  getFacilityById,
  getParentFacilities,
  setFacilitiesBreadcrumbs,
} from '../../../store/facility/actionCreators';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { getCurrentUserRole, getFacilityRoleById } from '../../../shared/utils';
import { Location } from '../../../store/facility/facilityTypes';
import DetailsTab from './components/DetailsTab';
import StaffTab from './components/StaffTab';
import PatientsTab from './components/PatientsTab';
import HubsTab from './components/HubsTab';
import NotesTab from './components/NotesTab';
import InsightsTab from './components/InsightsTab/InsightsTab';
import FacilityStatusIcon from '../components/FacilityStatusIcon';
import DeactivationConfirmation from './Deactivation/DeactivationConfirmation';
import { dimGrey, inactive, lightBlack, lightTeal } from '../../../mainTheme';
import { INSIGHTS_VIEW_ROLES, UserRoles } from '../../../shared/constants';

const tabStyles = {
  indicator: {
    backgroundColor: lightTeal,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    color: dimGrey,
    lineHeight: '20px',
    letterSpacing: '0.24px',
  },
  selected: {
    color: `${lightBlack} !important`,
  },
};

export type TabProps = {
  locations: Location[];
  parentFacilityId: number | undefined;
  locationId?: number;
  isFacilityAdmin?: boolean;
  facility?: any;
  onDeactivateFacility?: () => void;
};

type TabConfig = {
  id: string;
  label: string;
  component: React.ComponentType<TabProps>;
  showFor?: 'facility' | 'location' | 'both';
  // When set, the tab renders only for these roles; absent means every role
  // that can reach the page.
  visibleFor?: UserRoles[];
};

const tabConfig: TabConfig[] = [
  {
    id: 'details',
    label: 'Details',
    component: DetailsTab,
    showFor: 'both',
  },
  {
    id: 'locations',
    label: 'Locations',
    component: LocationsTab,
    showFor: 'facility',
  },
  {
    id: 'staff',
    label: 'Staff',
    component: StaffTab,
    showFor: 'both',
  },
  {
    id: 'patients',
    label: 'Patients',
    component: PatientsTab,
    showFor: 'both',
  },
  {
    id: 'hubs',
    label: 'Hubs',
    component: HubsTab,
    showFor: 'both',
  },
  {
    id: 'notes',
    label: 'Facility notes',
    component: NotesTab,
    showFor: 'facility',
  },
  {
    id: 'notes',
    label: 'Location notes',
    component: NotesTab,
    showFor: 'location',
  },
  {
    id: 'insights',
    label: 'Insights',
    component: InsightsTab,
    showFor: 'location',
    visibleFor: INSIGHTS_VIEW_ROLES,
  },
];

const FacilityDetails = () => {
  const navigate = useNavigate();
  const { facility_id, location_id } = useParams<{
    facility_id: string;
    location_id?: string;
  }>();
  const dispatch = useDispatch();

  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );
  const { parentFacilities, facilityInfo } = useSelector(
    (state: RootState) => state.facility,
  );
  const currentRole = dashboardUserInfo?.facility_role_id
    ? getFacilityRoleById(dashboardUserInfo.facility_role_id)
    : null;
  const isFacilityAdmin = currentRole === UserRoles.FACILITY_ADMIN;
  const viewerRole = getCurrentUserRole(dashboardUserInfo);

  const [selectedTab, setSelectedTab] = useState(0);
  const [isParentFacilitiesLoading, setIsParentFacilitiesLoading] =
    useState(false);
  const [showDeactivation, setShowDeactivation] = useState(false);

  useEffect(() => {
    if (isFacilityAdmin) {
      if (!facilityInfo) {
        dispatch(getFacilityById(Number(location_id)));
      }
    } else if (!parentFacilities) {
      setIsParentFacilitiesLoading(true); // this is to show loading spinner to avoid conflict with modal
      (async () => {
        try {
          await dispatch(getParentFacilities() as any);
        } finally {
          setIsParentFacilitiesLoading(false);
        }
      })();
    }
  }, [dispatch, parentFacilities, facilityInfo, location_id, isFacilityAdmin]);

  const filteredTabs = useMemo(() => {
    const viewType = location_id ? 'location' : 'facility';
    return tabConfig.filter(
      (tab) =>
        (tab.showFor === 'both' || tab.showFor === viewType) &&
        (!tab.visibleFor || tab.visibleFor.includes(viewerRole)),
    );
  }, [location_id, viewerRole]);

  useEffect(() => {
    setSelectedTab(0);
  }, [location_id]);

  // Use facilityInfo for admin, parentFacilities for others to keep consistent structure
  const facility: any = useMemo(() => {
    if (isFacilityAdmin && facilityInfo) {
      // Normalize: treat facilityInfo as a single-location facility
      return {
        ...facilityInfo,
        locations: [
          {
            ...facilityInfo,
            id: facilityInfo.id,
            name: facilityInfo.name,
            address: facilityInfo.address,
            address2: facilityInfo.address2,
            city: facilityInfo.city,
            state: facilityInfo.state,
            zip_code: facilityInfo.zip_code,
            phone: facilityInfo.phone,
            email: facilityInfo.email,
            total_devices: facilityInfo.total_devices,
            total_patients: facilityInfo.total_patients,
            created_at: facilityInfo.created_at,
            med_window_time_sec: facilityInfo.med_window_time_sec,
            status: facilityInfo.status,
          },
        ],
      };
    }
    return parentFacilities?.find(
      (pf) => pf.parent_facility_id.toString() === facility_id,
    );
  }, [isFacilityAdmin, facilityInfo, parentFacilities, facility_id]);

  const locationStatus = useMemo(() => {
    if (location_id && facility?.locations) {
      const location = facility.locations.find(
        (loc: { id: { toString: () => string } }) =>
          loc.id.toString() === location_id,
      );
      return location?.status || 'offline';
    }
    return null;
  }, [location_id, facility]);

  useEffect(() => {
    if (facility) {
      const crumbs = [
        {
          label: isFacilityAdmin ? 'Facility' : 'Facilities',
          path: '/v2/facilities',
        },
        {
          label: facility.parent_facility_name,
          path: `/v2/facilities/${facility.parent_facility_id}`,
        },
      ];
      if (location_id) {
        const location = facility.locations.find(
          (loc: { id: { toString: () => string } }) =>
            loc.id.toString() === location_id,
        );
        if (location) {
          crumbs.push({
            label: location.name,
            path: `/v2/facilities/${facility.parent_facility_id}/location/${location_id}`,
          });
        }
      }
      dispatch(setFacilitiesBreadcrumbs(crumbs));
    }
    // clear on unmount:
    return () => {
      dispatch(setFacilitiesBreadcrumbs([]));
    };
  }, [facility, dispatch, location_id]);

  if (isParentFacilitiesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {!isFacilityAdmin && ( // no Back button for location admin
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            border: `1px solid ${inactive}`,
            borderRadius: '10px',
            width: '60px',
            height: '40px',
            '&:hover': { border: `1px solid ${inactive}` },
          }}
        >
          Back
        </Button>
      )}

      <Box pt={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            {location_id && (
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.2px',
                  color: lightBlack,
                  mb: 0.5,
                }}
              >
                {facility?.parent_facility_name}
              </Typography>
            )}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '22px',
                  lineHeight: '30px',
                  letterSpacing: '0.36px',
                  color: lightBlack,
                }}
              >
                {location_id
                  ? facility?.locations.find(
                      (loc: { id: { toString: () => string } }) =>
                        loc.id.toString() === location_id,
                    )?.name
                  : facility?.parent_facility_name}
              </Typography>
              {location_id && (
                <FacilityStatusIcon
                  status={locationStatus as 'ok' | 'warning' | 'critical'}
                />
              )}
            </Box>
          </Box>

          {location_id && filteredTabs[selectedTab]?.id === 'details' && (
            <Button
              variant="contained"
              color="error"
              data-testid="deactivate-location-button"
              onClick={() => setShowDeactivation(true)}
            >
              Deactivate location
            </Button>
          )}
        </Box>

        <Divider sx={{ mt: 2, mb: 2 }} />

        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': tabStyles.indicator,
            '& .MuiTab-root': tabStyles.tab,
            '& .Mui-selected': tabStyles.selected,
          }}
        >
          {filteredTabs.map(({ label }) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
        <Box sx={{ borderBottom: 1, borderColor: inactive }} />
        <Box mt={2}>
          {filteredTabs[selectedTab]?.component &&
            React.createElement(filteredTabs[selectedTab].component, {
              locations: facility?.locations || [],
              parentFacilityId: facility?.parent_facility_id,
              locationId: location_id ? parseInt(location_id) : undefined,
              isFacilityAdmin,
              facility,
              onDeactivateFacility: () => setShowDeactivation(true),
            })}
        </Box>
      </Box>

      {showDeactivation && (
        <DeactivationConfirmation
          type={location_id ? 'location' : 'facility'}
          facilityId={
            facility?.parent_facility_id || parseInt(facility_id ?? '0')
          }
          locationId={location_id ? parseInt(location_id) : undefined}
          onClose={() => setShowDeactivation(false)}
          onSuccess={() => {
            setShowDeactivation(false);
            if (location_id) {
              navigate(`/v2/facilities/${facility?.parent_facility_id}`);
            } else {
              navigate('/v2/facilities');
            }
            dispatch(getParentFacilities());
          }}
        />
      )}
    </Box>
  );
};

export default FacilityDetails;
